import { Typography } from "antd";
import { clsx } from "clsx";

import styles from "./RVStatusBadge.module.scss";

/**
 * Badge status constants for different states and types.
 * Each status maps to a corresponding CSS class for visual styling.
 *
 * Available statuses:
 * - OTHER: Default/neutral status
 * - ERROR: Error or failure state
 * - PENDING: Awaiting action or processing
 * - ACTIVE: Currently active/enabled
 * - INACTIVE: Currently inactive/disabled
 * - IN_PROGRESS: Work in progress
 * - DONE: Completed successfully
 * - SUBSCRIBED: Subscription active
 * - CONNECTED: Connection established
 * - UNSUBSCRIBED: Subscription cancelled
 * - DISCONNECTED: Connection lost
 * - DYNAMIC: Dynamic configuration
 * - STATIC: Static configuration
 * - TEMPLATE: Template type
 * - TAG: Tag/label type
 * - VIEW_ONLY: Read-only access
 * - DEACTIVATED: Deactivated state
 */
export const BADGE_STATUS = {
    OTHER: "",
    ERROR: "error",
    PENDING: "pending",
    ACTIVE: "active",
    INACTIVE: "inactive",
    IN_PROGRESS: "inProgress",
    DONE: "done",
    SUBSCRIBED: "subscribed",
    CONNECTED: "connected",
    UNSUBSCRIBED: "unsubscribed",
    DISCONNECTED: "disconnected",
    DYNAMIC: "dynamic",
    STATIC: "static",
    TEMPLATE: "template",
    TAG: "tag",
    VIEW_ONLY: "viewOnly",
    DEACTIVATED: "deactivated",
} as const;

/**
 * Type representing valid badge status values.
 */
export type BadgeStatus = (typeof BADGE_STATUS)[keyof typeof BADGE_STATUS];

/**
 * Props for the RVStatusBadge component.
 */
type RVStatusBadgeProps = {
    /** The text or JSX element to display inside the badge */
    content: string | JSX.Element;
    /** The status type that determines the badge's visual styling */
    status: BadgeStatus;
    /** Optional additional CSS class name for custom styling */
    className?: string;
    /** Additional props to spread onto the container div */
    [x: string]: any;
};

/**
 * A styled badge component for displaying status information.
 *
 * The badge applies different visual styles based on the status prop,
 * with color and styling defined in the corresponding SCSS module.
 * Each status has a distinct color scheme to quickly convey state.
 *
 * @param props - Component props
 * @param props.content - The text or element to display in the badge
 * @param props.status - The status type that determines the badge styling
 * @param props.className - Optional additional CSS class name
 *
 * @example
 * ```tsx
 * // String content
 * <RVStatusBadge
 *   content="Active"
 *   status={BADGE_STATUS.ACTIVE}
 * />
 *
 * // JSX content
 * <RVStatusBadge
 *   content={<span>Processing <Spin size="small" /></span>}
 *   status={BADGE_STATUS.IN_PROGRESS}
 * />
 *
 * // With custom styling
 * <RVStatusBadge
 *   content="Custom"
 *   status={BADGE_STATUS.OTHER}
 *   className="my-custom-badge"
 * />
 * ```
 *
 * @todo Consider migrating to Ant Design Tag component for consistency with design system
 */
export function RVStatusBadge({
    className = "",
    content = "",
    status = BADGE_STATUS.PENDING,
    ...props
}: RVStatusBadgeProps): JSX.Element {
    // todo, should this be using the ANTD Tag component?
    return (
        <div className={styles.container} {...props}>
            <Typography.Text className={clsx(styles.status, className, styles[status])} {...props}>
                {content}
            </Typography.Text>
        </div>
    );
}
